import {pipe} from "mu-lambda";

import {stripMarketingParams} from "../strip-marketing-params.ts";
import {type UrlRule} from "../types.ts";

import {defaultParam} from "./default-param.ts";
import {extractParamFromCurrentValue} from "./extract-param-from-current-value.ts";
import {mapParam} from "./map-param.ts";

/**
 * Initialize the continue query param.
 *
 * A continue query param is used to provide a URL to redirect to after
 * a certain action is completed, such as logging in. This rule will
 * initialize the continue query param to the current URL if it is not
 * already set. If it is already set, it will extract the continue param
 * value from the currently set value, if there is one, and use that.
 * This also strips any marketing parameters from the continue URL.
 *
 * @param paramName The name of the param to use as the continue param.
 * Defaults to "continue".
 * @returns A URL rule that defaults the given param to the current URL, if
 * it is not already sets, then extracts the the same param from that URL,
 * it it is there, and then also strips marketing params from the URL in the
 * param.
 */
export const initializeContinueParam = (
    paramName: string = "continue",
): UrlRule =>
    pipe(
        defaultParam(
            paramName,
            () => location.pathname + location.search + location.hash,
        ),
        extractParamFromCurrentValue(paramName),
        mapParam(paramName, (v) => {
            if (v) {
                try {
                    // Clean up the URL to remove querystring parameters we
                    // don't want to stick through navigation. This, at the
                    // moment, applies to marketing query params that we don't
                    // want.
                    const continueUrl = stripMarketingParams(
                        new URL(v, location.origin),
                    );
                    return (
                        continueUrl.pathname +
                        continueUrl.search +
                        continueUrl.hash
                    );
                } catch (e) {
                    // It mustn't be a URL. Fallback silently.
                }
            }
            return v;
        }),
    );
